import React, { createContext, useContext, useCallback, useState, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

const ConfirmationContext = createContext(null);

export function useConfirmation() {
  const ctx = useContext(ConfirmationContext);
  if (!ctx) throw new Error('useConfirmation must be used within ConfirmationProvider');
  return ctx;
}

export function ConfirmationProvider({ children }) {
  const [state, setState] = useState({ open: false, title: '', description: '', confirmText: 'Delete', variant: 'destructive' });
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title = 'Are you sure?', description = 'This action cannot be undone.', confirmText = 'Delete', variant = 'destructive' } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, title, description, confirmText, variant });
    });
  }, []);

  const handleConfirm = () => {
    setState(prev => ({ ...prev, open: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  };

  const handleCancel = () => {
    setState(prev => ({ ...prev, open: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  };

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent data-testid="confirmation-dialog" className="sm:max-w-[440px]">
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full ${state.variant === 'destructive' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                <AlertTriangle className={`h-5 w-5 ${state.variant === 'destructive' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
              </div>
              <div className="flex-1">
                <AlertDialogTitle data-testid="confirmation-dialog-title" className="text-base font-semibold">{state.title}</AlertDialogTitle>
                <AlertDialogDescription data-testid="confirmation-dialog-description" className="mt-1 text-sm text-muted-foreground">{state.description}</AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel data-testid="confirmation-dialog-cancel" onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirmation-dialog-confirm"
              onClick={handleConfirm}
              className={state.variant === 'destructive' ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600' : ''}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationContext.Provider>
  );
}
